import React, { useEffect, useState } from 'react';
import ModalPortal from './ModalPortal';
import { X, Save, Percent } from 'lucide-react';

const TYPES = [
  ['ventes', 'Ventes (POS)'],
  ['commandes', 'Commandes'],
  ['acomptes', 'Acomptes'],
  ['reparations', 'Réparations'],
  ['factures', 'Factures'],
  ['devis', 'Devis'],
  ['b2b', 'B2B'],
  ['deplacement', 'Déplacement'],
];

const DEFAULTS = TYPES.reduce((acc, [k]) => {
  acc[k] = { enabled: false, rate_percent: 0, rate_fixed: 0 };
  return acc;
}, {});

export default function CommissionsModal({ user, api, onClose, onSaved }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get(`/admin/users/${user.id}/commissions-config`).then(r => {
      setConfig(JSON.parse(JSON.stringify(r.data.commissions || DEFAULTS)));
    }).catch(() => setConfig(DEFAULTS));
  }, [user, api]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/commissions-config`, { commissions: config });
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      alert("Erreur lors de la sauvegarde : " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  if (!user) return null;

  return (
    <ModalPortal isOpen={!!user}>
      <div className="bg-gray-900 border border-amber-500/40 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col"
           data-testid="commissions-modal">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Percent className="w-5 h-5 text-amber-400" />
            Commissions de <span className="text-amber-400">{user.username}</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-commissions-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {!config ? (
            <div className="text-center text-gray-400 py-12">Chargement…</div>
          ) : (
            TYPES.map(([t, label]) => {
              const c = config[t] || { enabled: false, rate_percent: 0, rate_fixed: 0 };
              const exemple = (((c.rate_percent || 0) * 100) / 100 + (c.rate_fixed || 0)).toFixed(2);
              return (
                <div key={t} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <label className="flex items-center justify-between mb-2 cursor-pointer">
                    <span className="font-semibold">{label}</span>
                    <input
                      type="checkbox"
                      checked={c.enabled}
                      onChange={e => setConfig({ ...config, [t]: { ...c, enabled: e.target.checked } })}
                      className="w-5 h-5 accent-amber-500"
                      data-testid={`commission-toggle-${t}`}
                    />
                  </label>
                  <div className={`grid grid-cols-2 gap-3 ${c.enabled ? '' : 'opacity-40 pointer-events-none'}`}>
                    <div>
                      <label className="text-xs text-gray-400">Pourcentage</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number" min="0" max="50" step="0.5"
                          value={c.rate_percent || 0}
                          onChange={e => setConfig({ ...config, [t]: { ...c, rate_percent: parseFloat(e.target.value) || 0 } })}
                          className="w-full bg-gray-800 border border-white/10 rounded px-2 py-1 text-right"
                          data-testid={`commission-pct-${t}`}
                        />
                        <span className="text-gray-400 text-sm">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Montant fixe</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number" min="0" step="0.5"
                          value={c.rate_fixed || 0}
                          onChange={e => setConfig({ ...config, [t]: { ...c, rate_fixed: parseFloat(e.target.value) || 0 } })}
                          className="w-full bg-gray-800 border border-white/10 rounded px-2 py-1 text-right"
                          data-testid={`commission-fix-${t}`}
                        />
                        <span className="text-gray-400 text-sm">€</span>
                      </div>
                    </div>
                  </div>
                  {c.enabled && (
                    <div className="text-xs text-amber-400 mt-2">
                      💡 Sur une vente de 100 € → commission = <strong>{exemple} €</strong>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="flex gap-2 justify-end p-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 rounded">Annuler</button>
          <button
            onClick={save}
            disabled={saving || !config}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-semibold rounded disabled:opacity-50"
            data-testid="save-commissions-btn"
          >
            <Save className="w-4 h-4" /> {saving ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}
